import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LANGUAGE_COLUMN_CODES = {
  arabic: 'ar', bengali: 'bn', chinese: 'zh', czech: 'cs', danish: 'da',
  dutch: 'nl', english: 'en', filipino: 'fil', finnish: 'fi', french: 'fr',
  german: 'de', greek: 'el', gujarati: 'gu', hebrew: 'he', hindi: 'hi',
  hungarian: 'hu', indonesian: 'id', italian: 'it', japanese: 'ja',
  kannada: 'kn', katakana: 'ja', korean: 'ko', malay: 'ms', malayalam: 'ml',
  marathi: 'mr', norwegian: 'no', odia: 'or', persian: 'fa', polish: 'pl',
  portuguese: 'pt', punjabi: 'pa', romanian: 'ro', russian: 'ru',
  spanish: 'es', swahili: 'sw', swedish: 'sv', tamil: 'ta', telugu: 'te',
  thai: 'th', turkish: 'tr', urdu: 'ur', vietnamese: 'vi',
};

function languageCodeForColumn(column) {
  const normalized = String(column)
    .trim()
    .toLowerCase()
    .replace(/\s+(sound|pronunciation)$/, '');
  return LANGUAGE_COLUMN_CODES[normalized] || null;
}

function cellsForRow(row) {
  if (Array.isArray(row)) return row;
  return Array.isArray(row?.cells) ? row.cells : [];
}

function filterForLanguagePair(data, nativeCode, targetCode, nativeLabel, targetLabel) {
  if (!data?.sets || !nativeCode || !targetCode) return data;
  const comparisonSets = [];
  const otherSets = [];

  data.sets.forEach((set) => {
    const languageCodes = set.columns.map(languageCodeForColumn);
    const isComparisonSet = /^Set\s+/i.test(set.title || '')
      || languageCodes.filter(Boolean).length >= 2;
    const keptIndexes = set.columns
      .map((_, index) => index)
      .filter((index) => index < 2
        || !languageCodes[index]
        || languageCodes[index] === nativeCode
        || languageCodes[index] === targetCode);
    const filteredSet = {
      ...set,
      title: isComparisonSet
        ? `${targetLabel || targetCode.toUpperCase()} · ${nativeLabel || nativeCode.toUpperCase()}`
        : set.title,
      columns: keptIndexes.map((index) => set.columns[index]),
      rows: set.rows.map((row) => {
        const cells = cellsForRow(row);
        return keptIndexes
          .filter((index) => index < cells.length)
          .map((index) => cells[index]);
      }),
      hasNativeColumn: languageCodes.includes(nativeCode),
    };
    if (isComparisonSet) comparisonSets.push(filteredSet);
    else otherSets.push(filteredSet);
  });

  const selectedComparison = comparisonSets.find((set) => set.hasNativeColumn)
    || comparisonSets[0];
  const cleanSet = ({ hasNativeColumn, ...set }) => set;
  return {
    ...data,
    title: `${targetLabel || targetCode.toUpperCase()} Alphabet`,
    subtitle: `Each letter with ${nativeLabel || nativeCode.toUpperCase()} pronunciation`,
    sets: [
      ...(selectedComparison ? [cleanSet(selectedComparison)] : []),
      ...otherSets.map(cleanSet),
    ],
  };
}

function PronunciationTable({ title, columns, rows, targetDir, speak }) {
  return (
    <section className="mb-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-violet-500 px-4 py-3 sm:px-5 sm:py-4">
        <h2 className="min-w-0 flex-1 text-sm font-semibold leading-relaxed text-white sm:text-base">
          {title}
        </h2>
        <span className="shrink-0 rounded-full border border-white/25 bg-white/15 px-2.5 py-1 text-xs font-semibold text-white">
          {rows.length}
        </span>
      </div>

      <div className="sm:hidden">
        {rows.map((row, ri) => {
          const cells = cellsForRow(row);
          return (
            <article
              key={row?.id || ri}
              className={`flex items-start gap-3.5 border-b border-slate-200 px-3.5 py-3.5 last:border-b-0 ${
                ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
              }`}
            >
              <button
                type="button"
                dir={targetDir}
                onClick={() => speak(cells[0])}
                aria-label={`Listen to ${cells[0]}`}
                title="Listen"
                className="relative flex min-h-[58px] w-[72px] shrink-0 items-center justify-center rounded-xl border border-violet-200 bg-violet-50 px-2 py-2.5 text-center transition hover:bg-violet-100"
              >
                <span className="text-2xl font-medium leading-relaxed text-violet-800">
                  {cells[0]}
                </span>
                <span
                  aria-hidden="true"
                  className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-600 text-[9px] leading-none text-white shadow"
                >
                  🔊
                </span>
              </button>
              <dl className="min-w-0 flex-1 space-y-2.5">
                {columns.slice(1).map((column, ci) => (
                  <div key={`${column}-${ci}`}>
                    <dt className="mb-0.5 text-[10px] font-bold uppercase leading-tight tracking-wider text-slate-500">
                      {column}
                    </dt>
                    <dd
                      className={`break-words leading-relaxed ${
                        ci === 0
                          ? 'text-[15px] font-medium text-slate-900'
                          : 'text-[13px] font-normal text-slate-700'
                      }`}
                    >
                      {cells[ci + 1] ?? ''}
                    </dd>
                  </div>
                ))}
              </dl>
            </article>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-indigo-50 border-b border-slate-200">
              {columns.map((col) => (
                <th
                  key={col}
                  className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-indigo-700"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const cells = cellsForRow(row);
              return (
              <tr
                key={row?.id || ri}
                className={`border-b border-slate-100 transition-colors hover:bg-indigo-50/60 ${
                  ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
                }`}
              >
                {cells.map((cell, ci) => (
                  <td
                    key={ci}
                    dir={ci === 0 ? targetDir : undefined}
                    className={`px-4 py-3 align-middle ${
                      ci === 0
                        ? 'w-20 text-center'
                        : ci === 1
                        ? 'min-w-[110px] text-sm font-medium text-slate-900'
                        : 'min-w-[110px] text-sm font-normal leading-relaxed text-slate-700'
                    }`}
                  >
                    {ci === 0 ? (
                      <button
                        type="button"
                        onClick={() => speak(cell)}
                        aria-label={`Listen to ${cell}`}
                        title="Listen"
                        className="relative inline-flex items-center justify-center rounded-lg px-2 py-1 transition hover:bg-violet-50"
                      >
                        <span className="text-2xl font-medium leading-relaxed text-violet-800">
                          {cell}
                        </span>
                        <span
                          aria-hidden="true"
                          className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-600 text-[9px] leading-none text-white shadow"
                        >
                          🔊
                        </span>
                      </button>
                    ) : (
                      cell
                    )}
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function AlphabetTables({
  data,
  chapters,
  nativeCode,
  targetCode,
  nativeLabel,
  targetLabel,
  nativeDir = 'ltr',
  targetDir = 'ltr',
  targetTts,
}) {
  const { t } = useTranslation();
  if (!data) return null;
  const filteredData = filterForLanguagePair(
    data,
    nativeCode,
    targetCode,
    nativeLabel,
    targetLabel,
  );
  const pairData = {
    ...filteredData,
    title: t('learn.alphabetTitle', { lang: targetLabel || targetCode?.toUpperCase() }),
    subtitle: t('learn.alphabetWithNative', { native: nativeLabel || nativeCode?.toUpperCase() }),
  };
  const visibleSets = pairData.sets;
  const speak = (text) => {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = targetTts || targetCode;
    window.speechSynthesis.speak(utterance);
  };
  const quickLetters = (visibleSets[0]?.rows || [])
    .map((row) => cellsForRow(row)[0])
    .filter(Boolean);
  return (
    <div className="p-4 md:p-6 bg-slate-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-slate-800 leading-tight">{pairData.title}</h1>
        {pairData.subtitle && (
          <p className="mt-1 text-slate-500 text-sm">{pairData.subtitle}</p>
        )}
        {chapters && chapters.length > 1 ? (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('journeyPages.startInteractiveLesson')}</p>
            {chapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/learn/${targetCode}/alphabet/lesson?chapter=${encodeURIComponent(chapter.id)}`}
                className="flex items-center gap-3 rounded-xl border border-violet-100 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-violet-50 text-xs font-black text-violet-700">{chapter.chapterNumber}</span>
                {chapter.label}
              </Link>
            ))}
          </div>
        ) : (
          <Link
            to={`/learn/${targetCode}/alphabet/lesson`}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-violet-700"
          >
            ▶ {t('journeyPages.startInteractiveLesson')}
          </Link>
        )}
      </div>
      {pairData.localizedIntroduction && (
        <section dir={nativeDir} className="mb-6 overflow-hidden rounded-2xl border border-indigo-200 bg-white shadow-sm">
          <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-5 py-4">
            <p className="text-xs font-semibold uppercase leading-relaxed tracking-widest text-indigo-600">{pairData.localizedIntroduction.title}</p>
          </div>
          <div className="space-y-3 px-5 py-5 text-sm leading-relaxed text-slate-700">
            <p>{pairData.localizedIntroduction.overview}</p>
            <p>{pairData.localizedIntroduction.countSummary}</p>
            <p>{pairData.localizedIntroduction.specialSymbolsSummary}</p>
            <p className="rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 font-semibold text-violet-900">
              {pairData.localizedIntroduction.comparison}
            </p>
          </div>
        </section>
      )}
      {quickLetters.length > 0 && (
        <section className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
              Quick Reference
            </h2>
          </div>
          <div className="grid grid-cols-5 gap-2 p-4 sm:gap-3">
            {quickLetters.map((letter, i) => {
              const lower = letter.toLowerCase();
              const display = lower === letter ? letter : `${letter} ${lower}`;
              return (
                <button
                  key={`${letter}-${i}`}
                  type="button"
                  dir={targetDir}
                  onClick={() => speak(letter)}
                  aria-label={`Listen to ${letter}`}
                  title="Listen"
                  className="relative flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-1 py-3 text-base font-medium text-slate-800 transition hover:border-indigo-300 hover:bg-indigo-50 sm:text-xl"
                >
                  {display}
                  <span
                    aria-hidden="true"
                    className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[9px] leading-none text-white shadow"
                  >
                    🔊
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-800">Explanation</h2>
        <p className="mt-1 text-sm text-slate-500">
          Full pronunciation guide with IPA, example words, and meanings.
        </p>
      </div>
      {visibleSets.map((set) => (
        <PronunciationTable
          key={set.title}
          title={set.title}
          columns={set.columns}
          rows={set.rows}
          targetDir={targetDir}
          speak={speak}
        />
      ))}
    </div>
  );
}
